import { Modal, Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useAiCredits, { creditsOn } from '../hooks/useAiCredits';
import './limitExceededModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faXmark } from '@fortawesome/free-solid-svg-icons';

const LimitExceededModal = ({ open, onClose, bots = [], onUpgrade }) => {
  const { t } = useTranslation();
  // AI credits on (backend controller/credits): the agents ran out of AI credits, not messages
  const inCredits = creditsOn(useAiCredits(bots[0]?.id));
  const safeName = (b) => b?.name || b?.domain_name || t('billing_unnamed_bot');

  const percent = (b) => {
    const used = Number(b?.current_message_count ?? 0);
    const limit = Number(b?.subscription_plan?.message_limit ?? 1);
    if (!Number.isFinite(used) || !Number.isFinite(limit) || limit <= 0) return 100;
    return Math.min(100, Math.max(0, Math.round((used / limit) * 100)));
  };

  const handleUpgrade = (bot) => {
    try {
      onUpgrade?.(bot);
    } catch (e) {
      console.error('Upgrade failed', e);
      alert(t('billing_upgrade_flow_error'));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: ['18px', '22px', '22px'],
          // Always leave a gutter on both sides: a fixed 720px at the sm
          // breakpoint overflowed viewports between 600-720px (no side space).
          // Width tracks the viewport (20px gutter each side), capped for desktop.
          width: 'calc(100% - 40px)',
          maxWidth: '480px',
          boxSizing: 'border-box',
          maxHeight: '85vh',
          overflowY: 'auto',
          outline: 'none',
        }}
      >
        <div className="modal-content lec-modal">
          {/* Floating close — no background, just the glyph (matches the
              playground locked card). */}
          <IconButton
            onClick={onClose}
            className="lec-close"
            sx={{
              position: 'absolute',
              top: 10,
              right: 12,
              color: '#6b6b78',
              '&:hover': { color: '#20202a', background: 'rgba(86, 22, 234, 0.08)' },
            }}
            aria-label={t('close')}
            title={t('close')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>

          {/* Hero — mirrors the playground "Message limit reached" card. */}
          <div className="lec-hero">
            <span className="lec-badge">
              <FontAwesomeIcon icon={faLock} />
            </span>
            <h2 className="lec-hero-title">{inCredits ? t('chat_credits_used_up_title') : t('chat_limit_reached_title')}</h2>
            <p className="lec-hero-sub">
              {inCredits
                ? t(bots.length === 1 ? 'billing_credits_used_up_subtitle_one' : 'billing_credits_used_up_subtitle',
                  { count: bots.length })
                : t('billing_message_limit_subtitle', { count: bots.length })}
            </p>
          </div>

          {/* One card per bot that hit its limit (multi-bot aware). */}
          <div className="lec-rows">
            {bots.map((b) => {
              const used = Number(b?.current_message_count ?? 0);
              const limit = Number(b?.subscription_plan?.message_limit ?? 0);
              const p = percent(b);

              return (
                <div className="lec-row" key={b.id}>
                  <div className="lec-row-head">
                    <span className="lec-bot-name" title={safeName(b)}>{safeName(b)}</span>
                    <span className="lec-plan-badge">
                      {b?.subscription_plan?.name || t('billing_plan_free')}
                    </span>
                  </div>

                  <div className="lec-progress">
                    <div className="lec-progress-bar" style={{ width: `${p}%` }} />
                  </div>

                  <div className="lec-row-foot">
                    <span className="lec-usage-numbers">
                      {used.toLocaleString('de-CH')} / {limit.toLocaleString('de-CH')}
                    </span>
                    <button
                      className="lec-upgrade-btn"
                      onClick={() => handleUpgrade(b)}
                      title={t('billing_upgrade_plan_tooltip')}
                    >
                      {t('upgrade')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default LimitExceededModal;
